import React from "react";
import { Button } from "@/components/ui/button";
import {
  Facebook,
  Linkedin,
  MessageCircle,
  Mail,
  Link as LinkIcon,
  Check,
} from "lucide-react";
import { toast } from "sonner";

// X/Twitter icon
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface SocialSharePanelProps {
  type: "event" | "challenge";
  title: string;
  url: string;
  description?: string;
  className?: string;
  compact?: boolean;
}

const SocialSharePanel: React.FC<SocialSharePanelProps> = ({
  type,
  title,
  url,
  description = "",
  className = "",
  compact = false,
}) => {
  const [copied, setCopied] = React.useState(false);

  const shareText = `Check out this ${type}: ${title}${description ? ` - ${description}` : ""}`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(url);

  const platforms = [
    {
      name: "X",
      icon: XIcon,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      color: "hover:bg-foreground/10",
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "hover:bg-blue-500/10 hover:text-blue-600",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "hover:bg-blue-700/10 hover:text-blue-700",
    },
    {
      name: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      color: "hover:bg-green-500/10 hover:text-green-600",
    },
    {
      name: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(`Join me at: ${title}`)}&body=${encodedText}%0A%0A${encodedUrl}`,
      color: "hover:bg-orange-500/10 hover:text-orange-600",
    },
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {platforms.slice(0, 3).map((platform) => (
          <Button
            key={platform.name}
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${platform.color}`}
            onClick={(e) => {
              e.stopPropagation();
              window.open(platform.href, "_blank", "width=600,height=400");
            }}
            title={`Share on ${platform.name}`}
          >
            <platform.icon className="h-4 w-4" />
          </Button>
        ))}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation();
            handleCopyLink();
          }}
          title="Copy link"
        >
          {copied ? <Check className="h-4 w-4 text-green-500" /> : <LinkIcon className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <p className="text-sm font-medium text-muted-foreground">Share on social media</p>
      <div className="flex flex-wrap gap-2">
        {platforms.map((platform) => (
          <Button
            key={platform.name}
            variant="outline"
            size="sm"
            className={platform.color}
            onClick={(e) => {
              e.stopPropagation();
              window.open(platform.href, "_blank", "width=600,height=400");
            }}
          >
            <platform.icon className="h-4 w-4" />
            <span>{platform.name}</span>
          </Button>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleCopyLink();
          }}
          className="hover:bg-muted"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 text-green-500" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <LinkIcon className="h-4 w-4" />
              <span>Copy Link</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SocialSharePanel;
